import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  AppstoreOutlined,
  BookOutlined,
  CheckOutlined,
  CreditCardOutlined,
  DashboardOutlined,
  DownOutlined,
  GlobalOutlined,
  KeyOutlined,
  LineChartOutlined,
  LogoutOutlined,
  MenuOutlined,
  MoonOutlined,
  QuestionCircleOutlined,
  FileProtectOutlined,
  SafetyOutlined,
  SettingOutlined,
  SunOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Breadcrumb,
  Button,
  ConfigProvider,
  Drawer,
  Dropdown,
  Layout,
  Menu,
  Select,
  Tooltip,
  type MenuProps,
} from "antd";
import zhCN from "antd/locale/zh_CN";
import type { User } from "@anyseal/contracts";
import { BrandMark } from "@anyseal/ui";
import { useAuth } from "./auth";
import { portalClient } from "./api";
import { useI18n } from "./i18n";
import { useWorkspace } from "./workspace";
import { useMinWidth } from "./useMediaQuery";
import { useTheme, type ThemeMode } from "./useTheme";
import { PanelHostProvider } from "./DetailsPanel";
import { themeFor, metrics } from "./theme";
import "./styles/shell.css";

const { Sider, Header, Content } = Layout;

// ── Navigation definition ────────────────────────────────────────────────────
// Flat list matching the Portal IA baseline in docs/PRODUCT.md §7.1.
// The rewards page stays reachable from Billing, not from primary nav.

const navItems = [
  { key: "overview",    path: "/dashboard",             label: { "zh-CN": "概览",       en: "Overview" },             icon: <DashboardOutlined /> },
  { key: "credentials", path: "/dashboard/credentials", label: { "zh-CN": "API 密钥",   en: "API keys" },             icon: <KeyOutlined /> },
  { key: "models",      path: "/dashboard/models",      label: { "zh-CN": "模型",       en: "Models" },               icon: <AppstoreOutlined /> },
  { key: "usage",       path: "/dashboard/usage",       label: { "zh-CN": "请求与用量", en: "Requests & usage" },     icon: <LineChartOutlined /> },
  { key: "billing",     path: "/dashboard/billing",     label: { "zh-CN": "账单与额度", en: "Billing & credit" },     icon: <CreditCardOutlined /> },
  { key: "docs",        path: "/dashboard/docs",        label: { "zh-CN": "开发文档",   en: "Developer docs" },       icon: <BookOutlined /> },
  { key: "security",    path: "/dashboard/security",    label: { "zh-CN": "账户与安全", en: "Account & security" },   icon: <SafetyOutlined /> },
  { key: "legal",       path: "/dashboard/legal",       label: { "zh-CN": "法律与隐私", en: "Legal & privacy" },  icon: <FileProtectOutlined /> },
] as const;

type NavKey = (typeof navItems)[number]["key"];

// ── Brand mark ───────────────────────────────────────────────────────────────
// PRODUCT.md §15.1 (2026-08-30): the official LonMax AI logo mark lives in
// packages/ui so Portal and Admin stay identical.

// ── Workspace popover ────────────────────────────────────────────────────────

type WorkspaceItem = { id: string; display_name: string };
type OrgItem = WorkspaceItem & { role?: string; slug?: string };
type ProjectItem = WorkspaceItem & { slug?: string };

const ADMIN_URL = import.meta.env.VITE_ADMIN_URL as string | undefined;

/** Platform operators (PRODUCT.md §4: the admin surface serves platform
 *  operators, not organization members). Org roles deliberately excluded —
 *  every self-registered user owns an organization. */
const PLATFORM_OPERATORS = new Set(["platform_owner", "platform_admin"]);

function WorkspacePopover({
  locale, user, organization, project, organizations, projects, setOrganizationId, setProjectId,
}: {
  locale: "zh-CN" | "en";
  user?: User; organization?: OrgItem; project?: ProjectItem;
  organizations: OrgItem[]; projects: ProjectItem[];
  setOrganizationId: (id: string) => void; setProjectId: (id: string) => void;
}) {
  const isChinese = locale === "zh-CN";
  const isPlatformOperator = (user?.platform_roles ?? []).some((role) => PLATFORM_OPERATORS.has(role));
  const roleLabel: Record<string, string> = {
    owner: isChinese ? "所有者" : "Owner",
    admin: isChinese ? "管理员" : "Admin",
    developer: isChinese ? "开发者" : "Developer",
    billing: isChinese ? "财务" : "Billing",
    viewer: isChinese ? "只读" : "Viewer",
  };

  return (
    <div className="portal-workspace-popover">
      {/* Current context summary */}
      <div className="portal-workspace-popover__head">
        <span>{isChinese ? "当前工作区" : "Current workspace"}</span>
        <strong>{project?.display_name ?? (isChinese ? "项目" : "Project")}</strong>
        {organization && (
          <div className="portal-workspace-popover__meta">
            <span className="portal-workspace-popover__org">
              {organization.display_name}
            </span>
            {organization.role && (
              <span className="portal-workspace-popover__role">
                {roleLabel[organization.role] ?? organization.role}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Switcher fields */}
      <div className="portal-workspace-popover__fields">
        <label>
          <span>{isChinese ? "组织" : "Organization"}</span>
          <Select
            value={organization?.id}
            options={organizations.map((o) => ({ value: o.id, label: o.display_name }))}
            onChange={setOrganizationId}
            showSearch optionFilterProp="label"
          />
        </label>
        <label>
          <span>{isChinese ? "项目" : "Project"}</span>
          <Select
            value={project?.id}
            options={projects.map((p) => ({ value: p.id, label: p.display_name }))}
            onChange={setProjectId}
            showSearch optionFilterProp="label"
          />
        </label>
      </div>

      {/* Admin link — platform operators only */}
      {isPlatformOperator && (
        <div className="portal-workspace-popover__footer">
          {ADMIN_URL ? (
            <a
              href={ADMIN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="portal-workspace-popover__admin-link"
            >
              <SettingOutlined />
              <span>{isChinese ? "打开管理后台" : "Open admin panel"}</span>
              <span className="portal-workspace-popover__ext-icon">↗</span>
            </a>
          ) : (
            <span className="portal-workspace-popover__admin-hint">
              {isChinese
                ? "在管理后台可创建和管理项目、成员与配置。"
                : "Create and manage projects, members, and settings in the admin panel."}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ── Sidebar panel ────────────────────────────────────────────────────────────

function SidebarPanel({
  locale, selectedKey, user, organization, project, organizations, projects,
  setOrganizationId, setProjectId, onNavigate, onSignOut,
}: {
  locale: "zh-CN" | "en"; selectedKey: NavKey;
  user?: User; organization?: OrgItem; project?: ProjectItem;
  organizations: OrgItem[]; projects: ProjectItem[];
  setOrganizationId: (id: string) => void; setProjectId: (id: string) => void;
  onNavigate: (key: NavKey) => void; onSignOut: () => void;
}) {
  const initial = (user?.display_name || user?.email || "U").trim().charAt(0).toUpperCase();
  const projectInitial = (project?.display_name || "D").trim().charAt(0).toUpperCase();
  const menuItems: MenuProps["items"] = navItems.map((item) => ({
    key: item.key,
    icon: item.icon,
    label: item.label[locale],
  }));

  return (
    <>
      <Link
        className="portal-rail__brand"
        to="/"
        aria-label={locale === "zh-CN" ? "LonMax AI 开发者门户" : "LonMax AI Developer Portal"}
      >
        <span className="portal-rail__mark"><BrandMark size={30} /></span>
        <span className="portal-rail__name">LonMax AI</span>
      </Link>

      <Dropdown
        trigger={["click"]}
        placement="bottomLeft"
        popupRender={() => (
          <WorkspacePopover
            locale={locale} user={user} organization={organization} project={project}
            organizations={organizations} projects={projects}
            setOrganizationId={setOrganizationId} setProjectId={setProjectId}
          />
        )}
      >
        <button type="button" className="portal-workspace" aria-label={locale === "zh-CN" ? "切换工作区" : "Switch workspace"}>
          <Avatar className="portal-workspace__avatar" size={38}>{projectInitial}</Avatar>
          <span className="portal-workspace__copy">
            <strong>{project?.display_name ?? (locale === "zh-CN" ? "默认项目" : "Default Project")}</strong>
            <small>{organization?.display_name ?? (locale === "zh-CN" ? "组织" : "Organization")}</small>
          </span>
          <DownOutlined className="portal-workspace__arrow" />
        </button>
      </Dropdown>

      <div className="portal-rail__nav">
        <Menu
          mode="inline"
          inlineIndent={14}
          items={menuItems}
          selectedKeys={[selectedKey]}
          onClick={({ key }) => onNavigate(key as NavKey)}
          aria-label={locale === "zh-CN" ? "主导航" : "Primary navigation"}
        />
      </div>

      <div className="portal-rail__foot">
        <Link className="portal-support" to="/dashboard/docs">
          <QuestionCircleOutlined />
          <span>{locale === "zh-CN" ? "帮助与支持" : "Support"}</span>
        </Link>
        <div className="portal-account" role="group" aria-label={locale === "zh-CN" ? "当前登录用户" : "Current signed-in user"}>
          <Avatar size={32}>{initial}</Avatar>
          <span className="portal-account__copy">
            <strong>{user?.display_name || (locale === "zh-CN" ? "当前用户" : "User")}</strong>
            <small title={user?.email}>{user?.email || (locale === "zh-CN" ? "加载中…" : "Loading…")}</small>
          </span>
          <Tooltip title={locale === "zh-CN" ? "退出登录" : "Sign out"} placement="top">
            <Button
              type="text" shape="circle" className="portal-signout"
              icon={<LogoutOutlined />}
              aria-label={locale === "zh-CN" ? "退出登录" : "Sign out"}
              onClick={onSignOut}
            />
          </Tooltip>
        </div>
      </div>
    </>
  );
}

// ── Main shell ───────────────────────────────────────────────────────────────

export function PortalShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [panelHost, setPanelHost] = useState<HTMLElement | null>(null);
  const panelHostRef = useCallback((node: HTMLDivElement | null) => setPanelHost(node), []);
  const { logout } = useAuth();
  const { locale, setLocale } = useI18n();
  const { mode: themeMode, resolved: resolvedTheme, setMode: setThemeMode } = useTheme();
  const { organizations, projects, organization, project, setOrganizationId, setProjectId } = useWorkspace();
  const me = useQuery({ queryKey: ["portal", "me"], queryFn: () => portalClient.get<User>("/api/portal/v1/me") });
  const location = useLocation();
  const navigate = useNavigate();
  const isDesktop = useMinWidth(metrics.railBreakpoint);
  const isDark = resolvedTheme === "dark";

  const currentKey = useMemo<NavKey>(() => {
    if (location.pathname === "/dashboard" || location.pathname === "/dashboard/") return "overview";
    return navItems
      .filter(({ path }) => path !== "/dashboard")
      .find(({ path }) => location.pathname === path || location.pathname.startsWith(`${path}/`))
      ?.key ?? "overview";
  }, [location.pathname]);

  const navigateTo = (key: NavKey) => {
    const item = navItems.find((e) => e.key === key);
    if (item) navigate(item.path);
    setMobileOpen(false);
  };

  const accountInitial = (me.data?.display_name || me.data?.email || "U").trim().charAt(0).toUpperCase();
  const accountMenu: MenuProps = {
    items: [
      { key: "security", icon: <UserOutlined />, label: locale === "zh-CN" ? "账户与安全" : "Account & security" },
      { type: "divider" },
      { key: "logout", icon: <LogoutOutlined />, danger: true, label: locale === "zh-CN" ? "退出登录" : "Sign out" },
    ],
    onClick: ({ key }) => {
      if (key === "security") navigate("/dashboard/security");
      if (key === "logout") void logout();
    },
  };

  // Language dropdown menu
  const langMenu: MenuProps = {
    selectedKeys: [locale],
    items: [
      { key: "zh-CN", label: "中文（简体）" },
      { key: "en",    label: "English" },
    ],
    onClick: ({ key }) => setLocale(key as "zh-CN" | "en"),
  };

  // Theme switcher menu
  const themeIconMap: Record<ThemeMode, React.ReactNode> = {
    light: <SunOutlined />,
    dark:  <MoonOutlined />,
    auto:  <CheckOutlined style={{ opacity: 0.5 }} />,
  };
  const themeMenu: MenuProps = {
    selectedKeys: [themeMode],
    items: [
      { key: "light", icon: <SunOutlined />,  label: locale === "zh-CN" ? "浅色" : "Light" },
      { key: "dark",  icon: <MoonOutlined />, label: locale === "zh-CN" ? "深色" : "Dark" },
      { key: "auto",  icon: <CheckOutlined />, label: locale === "zh-CN" ? "跟随系统" : "Auto" },
    ],
    onClick: ({ key }) => setThemeMode(key as ThemeMode),
  };

  const sidebarProps = {
    locale, selectedKey: currentKey, user: me.data,
    organization, project, organizations, projects,
    setOrganizationId, setProjectId,
    onNavigate: navigateTo, onSignOut: () => void logout(),
  };

  const currentItem = navItems.find((e) => e.key === currentKey);
  const portalTheme = themeFor(isDark);

  return (
    <ConfigProvider theme={portalTheme} locale={locale === "zh-CN" ? zhCN : undefined}>
      <Layout className={`portal-shell${isDark ? " portal-shell--dark" : ""}`}>

        {/* ── Desktop rail ── */}
        {isDesktop && (
          <Sider className="portal-rail" width={metrics.railWidth} theme="light">
            <SidebarPanel {...sidebarProps} />
          </Sider>
        )}

        {/* ── Mobile drawer ── */}
        {!isDesktop && (
          <Drawer
            className="portal-rail-drawer"
            placement="left"
            width={metrics.railWidth}
            open={mobileOpen}
            closable={false}
            onClose={() => setMobileOpen(false)}
            styles={{ body: { padding: 0 } }}
          >
            <div className="portal-rail__inner">
              <SidebarPanel {...sidebarProps} />
            </div>
          </Drawer>
        )}

        {/* ── White / dark main region ── */}
        <Layout className="portal-region">

          {/* Header — frosted glass */}
          <Header className="portal-header">
            {!isDesktop && (
              <Button
                type="text" icon={<MenuOutlined />}
                aria-label={locale === "zh-CN" ? "打开菜单" : "Open menu"}
                onClick={() => setMobileOpen(true)}
                style={{ marginRight: 8 }}
              />
            )}
            <Breadcrumb
              className="portal-header__crumb"
              separator="/"
              items={[
                { title: locale === "zh-CN" ? "开发者门户" : "Developer Portal" },
                { title: organization?.display_name ?? "LonMax AI" },
                { title: <strong>{currentItem?.label[locale] ?? project?.display_name ?? "—"}</strong> },
              ]}
            />
            <div className="portal-header__actions">
              <Link className="portal-header__link" to="/dashboard/docs">
                <BookOutlined />
                <span>{locale === "zh-CN" ? "文档" : "Docs"}</span>
              </Link>

              {/* Theme switcher */}
              <Dropdown menu={themeMenu} trigger={["click"]} placement="bottomRight">
                <Button
                  type="text"
                  className="portal-header__icon-btn"
                  aria-label={locale === "zh-CN" ? "切换主题" : "Switch theme"}
                  icon={themeIconMap[themeMode]}
                />
              </Dropdown>

              {/* Language switcher — globe icon; the dropdown names each language */}
              <Dropdown menu={langMenu} trigger={["click"]} placement="bottomRight">
                <Button
                  type="text"
                  className="portal-header__icon-btn"
                  aria-label={locale === "zh-CN" ? "切换语言" : "Switch language"}
                  icon={<GlobalOutlined />}
                />
              </Dropdown>

              {/* Account avatar menu */}
              <Dropdown menu={accountMenu} trigger={["click"]} placement="bottomRight">
                <Button
                  type="text" className="portal-header__avatar"
                  aria-label={locale === "zh-CN" ? "账户菜单" : "Account menu"}
                >
                  <Avatar size={30}>{accountInitial}</Avatar>
                </Button>
              </Dropdown>
            </div>
          </Header>

          {/* Content row: scrolling area + panel host */}
          <div className="portal-region__body">
            <PanelHostProvider value={panelHost}>
              <Content className="portal-content">
                <Outlet />
              </Content>
              <div className="portal-panel-host" ref={panelHostRef} />
            </PanelHostProvider>
          </div>

        </Layout>
      </Layout>
    </ConfigProvider>
  );
}


